// Token Refresh Manager - Atomic refresh handling to prevent race conditions
import axios from 'axios';

interface RefreshState {
  promise: Promise<any> | null;
  retryCount: number;
  lastAttempt: number;
}

class TokenRefreshManager {
  private state: RefreshState = {
    promise: null,
    retryCount: 0,
    lastAttempt: 0
  };
  
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second between retries
  private readonly REFRESH_COOLDOWN = 5000; // 5 seconds between refresh attempts
  
  /**
   * Atomic token refresh with race condition protection
   * Multiple concurrent calls will receive the same promise
   */
  async refresh(apiUrl: string): Promise<boolean> {
    const now = Date.now();
    
    // Check if we're within cooldown period and have exceeded retries
    if (now - this.state.lastAttempt < this.REFRESH_COOLDOWN && this.state.retryCount >= this.MAX_RETRIES) {
      return false;
    }
    
    // If refresh is already in progress, return existing promise
    if (this.state.promise) {
      try {
        return await this.state.promise;
      } catch (error) {
        // If the existing promise failed, we might want to try again
        // but only if we haven't exceeded retry limits
        if (this.state.retryCount >= this.MAX_RETRIES) {
          return false;
        }
      }
    }
    
    // Don't attempt refresh if we've exceeded max retries recently
    if (this.state.retryCount >= this.MAX_RETRIES) {
      return false;
    }
    
    // Create new refresh promise
    this.state.promise = this.executeRefresh(apiUrl);
    this.state.lastAttempt = now;
    
    try {
      const result = await this.state.promise;
      if (result) {
        this.state.retryCount = 0; // Reset retry count on success
      }
      return result;
    } catch (error) {
      this.state.retryCount++;
      return false; // Return false instead of throwing to prevent loops
    } finally {
      // Clear the promise after completion
      this.state.promise = null;
    }
  }
  
  private async executeRefresh(apiUrl: string): Promise<boolean> {
    // console.log('Executing token refresh...'); // DISABLED TO STOP SPAM
    
    try {
      const response = await axios.post(
        `${apiUrl}/auth/refresh`,
        {},
        {
          withCredentials: true,
          timeout: 10000,
          headers: {
            'X-Refresh-Request': 'true' // Mark as refresh request
          }
        }
      );
      
      if (response.data?.success) {
        // console.log('Token refresh successful'); // DISABLED TO STOP SPAM
        return true;
      }
      
      // console.error('Token refresh failed: Invalid response', response.data); // DISABLED TO STOP SPAM
      return false;
    } catch (error: any) {
      // console.error('Token refresh error:', { // DISABLED TO STOP SPAM
      //   message: error.message,
      //   status: error.response?.status,
      //   data: error.response?.data
      // });
      
      // Don't retry on 403 (forbidden) or 400 (bad request)
      if (error.response?.status === 403 || error.response?.status === 400) {
        // console.error('Token refresh permanently failed'); // DISABLED TO STOP SPAM
        this.state.retryCount = this.MAX_RETRIES; // Prevent further retries
      }
      
      throw error;
    }
  }
  
  /**
   * Reset the refresh manager state
   */
  reset() {
    this.state = {
      promise: null,
      retryCount: 0,
      lastAttempt: 0
    };
  }
  
  /**
   * Check if refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.state.promise !== null;
  }
  
  /**
   * Get current retry count
   */
  getRetryCount(): number {
    return this.state.retryCount;
  }
}

// Export singleton instance
export const refreshManager = new TokenRefreshManager();